import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PublicNoticePage from './common/PublicNoticePage';

/**
 * Either a signed `base64url(payload).base64url(sha256 hmac)` token or a
 * 10-character short code — the two shapes the backend mints.
 */
const TOKEN_PATTERN = /^(?:[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}|[A-Za-z0-9]{10})$/;
export const FALLBACK_DELAY_MS = 2000;
const MOBILE_UA_RE = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i;
const RESEND_MESSAGE =
  "Hi! The link to my email draft isn't working — could you send me a fresh one?";

interface DraftDestinations {
  desktop: string;
  mobile: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'redirecting'; target: string }
  | { kind: 'invalid'; whatsappUrl: string | null }
  | { kind: 'error' };

interface EmailDraftLinkPageProps {
  redirect?: (destination: string) => void;
}

const replaceLocation = (destination: string) =>
  window.location.replace(destination);

/** Phones and small tablets get the mobile web URL; everything else desktop. */
export const prefersMobileDestination = (): boolean => {
  const coarse =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  return (
    MOBILE_UA_RE.test(window.navigator.userAgent) ||
    (coarse && window.innerWidth < 900)
  );
};

const resendUrl = (whatsappUrl: string) =>
  `${whatsappUrl}?text=${encodeURIComponent(RESEND_MESSAGE)}`;

/**
 * `/d/:token` — a Dream-owned "Open draft" link from WhatsApp. Asks the API
 * which Gmail/Outlook URLs the token maps to, picks the one for this device
 * and navigates there. Stays blank unless the navigation stalls or the token
 * is bad: the happy path should not look like a Dream page.
 */
const EmailDraftLinkPage: React.FC<EmailDraftLinkPageProps> = ({
  redirect = replaceLocation,
}) => {
  const { token = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!TOKEN_PATTERN.test(token)) {
      setState({ kind: 'invalid', whatsappUrl: null });
      return undefined;
    }
    const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const controller = new AbortController();
    fetch(`${apiBase}/d/${encodeURIComponent(token)}`, {
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 404) {
          const body = (await res.json()) as { whatsappUrl?: string | null };
          setState({ kind: 'invalid', whatsappUrl: body.whatsappUrl ?? null });
          return;
        }
        if (!res.ok) throw new Error(`draft link ${res.status}`);
        const destinations = (await res.json()) as DraftDestinations;
        setState({
          kind: 'redirecting',
          target: prefersMobileDestination()
            ? destinations.mobile
            : destinations.desktop,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('[EmailDraftLink] Failed to resolve draft link:', error);
        setState({ kind: 'error' });
      });
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (state.kind !== 'redirecting') return undefined;
    redirect(state.target);
    const timeout = window.setTimeout(
      () => setStalled(true),
      FALLBACK_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [state, redirect]);

  if (state.kind === 'invalid') {
    return (
      <PublicNoticePage
        testId='email-draft-link-invalid'
        title="We couldn't find that draft"
        body="This link isn't one we recognise — it may be incomplete or out of date. Your secretary can send you a new one in seconds."
        actions={
          state.whatsappUrl && (
            <a
              className='public-notice__button'
              href={resendUrl(state.whatsappUrl)}
            >
              Ask your secretary to resend it
            </a>
          )
        }
      />
    );
  }

  if (state.kind === 'error') {
    return (
      <PublicNoticePage
        testId='email-draft-link-error'
        title="We couldn't open that draft right now"
        body='Something went wrong on our side. Please try the link again in a moment.'
        actions={
          <button
            type='button'
            className='public-notice__button'
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        }
      />
    );
  }

  if (state.kind === 'redirecting' && stalled) {
    return (
      <PublicNoticePage
        testId='email-draft-link-fallback'
        title='This is taking longer than expected'
        body="Your draft should have opened by now. If it hasn't, tap the button below to continue."
        actions={
          <a className='public-notice__button' href={state.target}>
            Tap here to continue
          </a>
        }
      />
    );
  }

  return <div className='public-notice' data-testid='email-draft-link-blank' />;
};

export default EmailDraftLinkPage;
